import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import VisitTracker from "@/components/VisitTracker";
import AnalyticsBootstrap from "@/components/AnalyticsBootstrap";
import SEOHead from "@/components/SEOHead";
import PropFirmPopup from "@/components/PropFirmPopup";
import CookieConsent from "@/components/CookieConsent";
import StickyCTA from "@/components/StickyCTA";
import MobileBottomNav from "@/components/MobileBottomNav";
import MatrixBackground from "@/components/MatrixBackground";
import ScrollToTop from "@/components/ScrollToTop";
import PageLoader from "@/components/PageLoader";
import TawkTo from "@/components/TawkTo";
import VersionWatcher from "@/components/VersionWatcher";
import ClickTracker from "@/components/ClickTracker";
import InstallPrompt from "@/components/InstallPrompt";
import SignupModalProvider from "@/components/SignupModalProvider";
import HomePage from "@/pages/HomePage";
import { prefetchAllIdle } from "@/lib/prefetch";

/* All non-essential pages are code-split. The initial chunk now only
   contains HomePage + always-on chrome (Matrix, Toaster). Per-route
   bundles load on demand in < 100ms, then are cached.

   info/index.jsx is shared across many small pages · webpack will
   give it a single chunk because the named-export lazy() calls all
   import the same module. That means the FIRST info-page nav fetches
   the chunk, every subsequent info-page nav is instant. */
const AccountComparisonPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/AccountComparisonPage"));
const LeveragePage       = lazy(() => import("@/pages/LeveragePage"));
const UnsubscribePage    = lazy(() => import("@/pages/UnsubscribePage"));
const SafetyOfFundsPage  = lazy(() => import("@/pages/SafetyOfFundsPage"));
const FundingPage        = lazy(() => import("@/pages/FundingPage"));
const AccountTypeDetailPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/AccountTypeDetailPage"));
const FirstDepositBonusPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/FirstDepositBonusPage"));
const FirstDepositBonus100Page = lazy(() => import("@/pages/FirstDepositBonus100Page"));
const Boost75Page = lazy(() => import("@/pages/Boost75Page"));
const CompanyNewsPage    = lazy(() => import("@/pages/CompanyNewsPage"));
const NewsArticlePage    = lazy(() => import("@/pages/NewsArticlePage"));
const MarketCategoryPage    = lazy(() => import("@/pages/MarketCategoryPage"));
const SymbolExplorerPage    = lazy(() => import("@/pages/SymbolExplorerPage"));
const MarketHoursPage       = lazy(() => import("@/pages/MarketHoursPage"));
const EconomicCalendarPage  = lazy(() => import("@/pages/EconomicCalendarPage"));
const SentimentPage         = lazy(() => import("@/pages/SentimentPage"));
const GlossaryPage          = lazy(() => import("@/pages/GlossaryPage"));
const SwapRatesPage         = lazy(() => import("@/pages/SwapRatesPage"));
const PlatformPage          = lazy(() => import("@/pages/PlatformPage"));
const VpsApplyPage          = lazy(() => import("@/pages/VpsApplyPage"));
const InfoPage              = lazy(() => import("@/pages/InfoPage"));
const AdminLoginPage     = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard     = lazy(() => import("@/pages/AdminDashboard"));
const AdminSignatures    = lazy(() => import("@/pages/admin/AdminSignatures"));
const SignaturePage      = lazy(() => import("@/pages/SignaturePage"));
const GoogleCallback     = lazy(() => import("@/pages/GoogleCallback"));
const LegacyRouteHandler = lazy(() => import("@/components/LegacyRouteHandler"));
const LegalPage          = lazy(() => import("@/pages/LegalPage"));
const LegalHubPage       = lazy(() => import("@/pages/LegalHubPage"));
const HowItWorksFullPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/HowItWorksFullPage"));
const ReviewsPage        = lazy(() => import("@/pages/ReviewsPage"));
const CalculatorFullPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CalculatorFullPage"));
const ContactFullPage    = lazy(() => import("@/pages/ContactFullPage"));
const FAQFullPage        = lazy(() => import(/* webpackPrefetch: true */ "@/pages/FAQFullPage"));
const PartnerFullPage    = lazy(() => import("@/pages/PartnerFullPage"));
const LiveSpreadsPage    = lazy(() => import("@/pages/LiveSpreadsPage"));

const info = (name) => lazy(() => import("@/pages/info").then((m) => ({ default: m[name] })));
const AboutPage              = lazy(() => import("@/pages/AboutPage"));
const GeneralRulesPage       = info("GeneralRulesPage");
const PayoutProtectorPage    = info("PayoutProtectorPage");
const WebTradingPage         = info("WebTradingPage");
const MobileTradingPage      = info("MobileTradingPage");
const AffiliateDashboardPage = info("AffiliateDashboardPage");

function App() {
  // Eagerly warm every route chunk during browser idle time. By the
  // time the user clicks a menu item, the chunk is already cached and
  // navigation feels instant.
  if (typeof window !== "undefined") prefetchAllIdle();
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <SiteSettingsProvider>
            <BrowserRouter>
            <SignupModalProvider>
            <MatrixBackground />
            <ScrollToTop />
            <VisitTracker />
            <AnalyticsBootstrap />
            <SEOHead />
            <PropFirmPopup />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/account-comparison" element={<AccountComparisonPage />} />
                <Route path="/account-types" element={<Navigate to="/account-comparison" replace />} />
                <Route path="/account-types/:slug" element={<AccountTypeDetailPage />} />
                <Route path="/first-deposit-bonus" element={<FirstDepositBonusPage />} />
                {/* HIDDEN · IB-only 100% variant · not linked from menu / footer / sitemap. */}
                <Route path="/vip-bonus-fr-100" element={<FirstDepositBonus100Page />} />
                <Route path="/tradeboost-75-2k" element={<Boost75Page />} />
                <Route path="/company-news" element={<CompanyNewsPage />} />
                <Route path="/company-news/:slug" element={<NewsArticlePage />} />
                <Route path="/markets/:slug" element={<MarketCategoryPage />} />
                <Route path="/platforms/mt5" element={<PlatformPage />} />
                <Route path="/platforms/mt5/*" element={<PlatformPage />} />
                <Route path="/platforms/vps" element={<PlatformPage />} />
                <Route path="/platforms/vps/apply" element={<VpsApplyPage />} />
                <Route path="/platforms/copy-trading" element={<PlatformPage />} />
                <Route path="/platforms/algo" element={<PlatformPage />} />
                <Route path="/regulation" element={<InfoPage />} />
                <Route path="/safety-of-funds" element={<SafetyOfFundsPage />} />
                <Route path="/why-ofinancial" element={<InfoPage />} />
                <Route path="/funding" element={<FundingPage />} />
                <Route path="/withdrawals" element={<InfoPage />} />
                <Route path="/leverage" element={<LeveragePage />} />
                <Route path="/swap-rates" element={<SwapRatesPage />} />
                <Route path="/symbols" element={<SymbolExplorerPage />} />
                <Route path="/glossary" element={<GlossaryPage />} />
                <Route path="/education" element={<Navigate to="/glossary" replace />} />
                <Route path="/market-hours" element={<MarketHoursPage />} />
                <Route path="/sentiment" element={<SentimentPage />} />
                <Route path="/economic-calendar" element={<EconomicCalendarPage />} />
                <Route path="/support" element={<InfoPage />} />
                <Route path="/live-spreads" element={<LiveSpreadsPage />} />
                <Route path="/legal" element={<LegalHubPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="/faq" element={<FAQFullPage />} />
                <Route path="/partner" element={<PartnerFullPage />} />
                <Route path="/how-it-works" element={<HowItWorksFullPage />} />
                <Route path="/general-rules" element={<GeneralRulesPage />} />
                <Route path="/affiliates" element={<Navigate to="/partner#introducer" replace />} />
                <Route path="/affiliates/influencers" element={<Navigate to="/partner#ambassador" replace />} />
                <Route path="/affiliates/dashboard" element={<AffiliateDashboardPage />} />
                <Route path="/ib" element={<Navigate to="/partner#introducer" replace />} />
                <Route path="/ambassador" element={<Navigate to="/partner#ambassador" replace />} />
                <Route path="/payout-protector" element={<PayoutProtectorPage />} />
                <Route path="/calculator" element={<CalculatorFullPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/contact" element={<ContactFullPage />} />
                <Route path="/signup" element={<HomePage />} />
                <Route path="/register" element={<HomePage />} />
                <Route path="/open-account" element={<HomePage />} />
                <Route path="/platforms/web" element={<WebTradingPage />} />
                <Route path="/platforms/mobile" element={<MobileTradingPage />} />
                {/* Legacy /info/* slugs that some old links / docs reference.
                    Redirect to the canonical pages so they don't 404. */}
                <Route path="/info/spreads"  element={<Navigate to="/live-spreads" replace />} />
                <Route path="/info/leverage" element={<Navigate to="/leverage" replace />} />
                <Route path="/info/swaps"    element={<Navigate to="/swap-rates" replace />} />
                <Route path="/info/symbols"  element={<Navigate to="/symbols" replace />} />
                <Route path="/info/funding"  element={<Navigate to="/funding" replace />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/signatures" element={<AdminSignatures />} />
                <Route path="/signature/:slug" element={<SignaturePage />} />
                <Route path="/auth/google" element={<GoogleCallback />} />
                {/* Catch-all 404 · must be LAST */}
                <Route path="*" element={<LegacyRouteHandler />} />
              </Routes>
            </Suspense>
            <StickyCTA />
            <MobileBottomNav />
            <TawkTo />
            <VersionWatcher />
            <ClickTracker />
            <InstallPrompt />
            <CookieConsent />
            <Toaster theme="dark" position="bottom-right" richColors />
            </SignupModalProvider>
          </BrowserRouter>
          </SiteSettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
